import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { InviteAcceptResult, InvitePeek, MyInvite, MyInvitesResponse } from '../api/types'
import { AuthCard } from '../components/AuthCard'
import { useSession } from '../context/SessionContext'
import {
  clearPendingInviteToken,
  getPendingInviteToken,
  setPendingInviteToken,
  withNext,
} from '../context/pendingInvite'

const ROLE_LABEL: Record<string, string> = { admin: 'Admin', member: 'Member', viewer: 'Viewer' }

/** Where sign-in / sign-up return to: the bare /invite page, which reads the token from memory. */
const RETURN_PATH = '/invite'

const MIN_PASSWORD = 8

// A brand-new invitee has no password. The Supabase invite email links to
// `/invite/<invite token>?token_hash=…&type=invite`; that GoTrue token_hash lets them set
// one (POST /auth/reset-password, type invite). Read once from the query string only:
// a `#access_token=…` session fragment (older default template) is never read, just
// stripped with the rest of the URL.
function readInviteTokenHash(search: string): string | null {
  const params = new URLSearchParams(search)
  if (params.get('type') !== 'invite') return null
  const v = params.get('token_hash')
  return v && v.trim() ? v.trim() : null
}

type SetPasswordState = 'idle' | 'loading' | 'error' | 'expired'

function SetPasswordCard({ peek, tokenHash }: { peek: InvitePeek; tokenHash: string }) {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [state, setState] = useState<SetPasswordState>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const signInPath = withNext('/login', RETURN_PATH)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (password.length < MIN_PASSWORD) {
      setState('error')
      setErrorMsg(`Password must be at least ${MIN_PASSWORD} characters.`)
      return
    }
    if (password !== confirm) {
      setState('error')
      setErrorMsg('The passwords don’t match.')
      return
    }
    setState('loading')
    setErrorMsg('')
    try {
      await apiFetch('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token_hash: tokenHash, new_password: password, type: 'invite' }),
      })
      // No cookie is issued: sign in with the new password, then come back to the
      // invite (its token stays in memory, as for any sign-in round trip).
      navigate(`/login?password_set=1&next=${encodeURIComponent(RETURN_PATH)}`, { replace: true })
    } catch (err) {
      const s = isApiError(err) ? err.status : 0
      if (s === 401) {
        setState('expired')
        return
      }
      setState('error')
      if (s === 422) {
        const d = isApiError(err) && typeof err.detail === 'string' ? err.detail : ''
        setErrorMsg(d || `Password does not meet requirements (at least ${MIN_PASSWORD} characters).`)
      } else setErrorMsg('Could not set your password. Please try again in a moment.')
    }
  }

  if (state === 'expired') {
    return (
      <AuthCard title="Link expired" footer={<Link to={signInPath}>Already set a password? Sign in</Link>}>
        <InviteSummary peek={peek} />
        <p role="alert" className="form-error">
          This set-password link has expired or was already used. Ask the person who invited
          you to send a new invite.
        </p>
      </AuthCard>
    )
  }

  const busy = state === 'loading'
  return (
    <AuthCard
      title="Set your password"
      subtitle={<>Choose a password for your Symone account, then sign in to join <strong>{peek.workspace_name}</strong>.</>}
      footer={<Link to={signInPath}>Already have a password? Sign in</Link>}
    >
      <InviteSummary peek={peek} />
      <form onSubmit={handleSubmit} aria-label="Set password form" noValidate>
        <div className="field">
          <label htmlFor="invite-password">Password</label>
          <input id="invite-password" type="password" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} disabled={busy} required minLength={MIN_PASSWORD}
            aria-describedby="invite-password-hint" />
          <span id="invite-password-hint" className="field-hint">At least {MIN_PASSWORD} characters.</span>
        </div>
        <div className="field">
          <label htmlFor="invite-confirm">Confirm password</label>
          <input id="invite-confirm" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} disabled={busy} required />
        </div>
        {state === 'error' && <p role="alert" className="form-error">{errorMsg}</p>}
        <button type="submit" className="btn btn--primary btn--block"
          disabled={busy || !password || !confirm} aria-busy={busy}>
          {busy ? 'Saving…' : 'Set password'}
        </button>
      </form>
    </AuthCard>
  )
}

// The public peek never echoes the invited email (no enumeration), so the page decides
// "is this invite for the signed-in account?" from GET /me/invites (pending invites for
// the caller's own email): the same workspace, role and expiry instant means it is.
// Record matching, not an authorization check: one key per invite.
function inviteKey(i: Pick<InvitePeek, 'workspace_name' | 'role' | 'expires_at'>): string {
  return [i.workspace_name, i.role, Date.parse(i.expires_at)].join('|')
}

function findMine(peek: InvitePeek, mine: MyInvite[]): MyInvite | undefined {
  const key = inviteKey(peek)
  return mine.find((m) => inviteKey(m) === key)
}

function acceptErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    if (err.status === 409) return 'This invite has already been used.'
    if (err.status === 404) return 'This invite is no longer valid. Ask for a new one.'
    if (err.status === 401) return 'Your session expired. Sign in again to accept.'
  }
  return errorMessage(err)
}

function InviteSummary({ peek }: { peek: InvitePeek }) {
  return (
    <dl className="invite-summary">
      <div><dt>Workspace</dt><dd>{peek.workspace_name}</dd></div>
      <div><dt>Role</dt><dd>{ROLE_LABEL[peek.role] ?? peek.role}</dd></div>
      {peek.invited_by_name && <div><dt>Invited by</dt><dd>{peek.invited_by_name}</dd></div>}
      <div><dt>Expires</dt><dd>{new Date(peek.expires_at).toLocaleDateString()}</dd></div>
    </dl>
  )
}

export function InviteView() {
  const { token: urlToken } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { status, session, recheck, switchWorkspace, logout } = useSession()
  const [token] = useState(() => urlToken ?? getPendingInviteToken())
  // Component state only, like the reset page; stripped from the address bar below.
  const [inviteTokenHash] = useState(() => readInviteTokenHash(location.search))
  const [mismatch, setMismatch] = useState(false)

  // Keep the token in memory for the sign-in round trip, then strip it, any
  // ?token_hash=…&type=invite, and any GoTrue #fragment from the address bar.
  useEffect(() => {
    if (urlToken) setPendingInviteToken(urlToken)
    if (urlToken || location.search || location.hash) navigate(RETURN_PATH, { replace: true })
  }, [urlToken, location.search, location.hash, navigate])

  const authed = status === 'authenticated'
  const peekQ = useQuery({
    queryKey: qk.invitePeek(token ?? ''),
    queryFn: () => apiFetch<InvitePeek>(`/auth/invites/${encodeURIComponent(token ?? '')}`),
    enabled: !!token,
    retry: false,
  })
  const mineQ = useQuery({
    queryKey: qk.myInvites,
    queryFn: () => apiFetch<MyInvitesResponse>('/me/invites'),
    enabled: authed && peekQ.isSuccess,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: () =>
      apiFetch<InviteAcceptResult>(`/auth/invites/${encodeURIComponent(token ?? '')}/accept`, { method: 'POST' }),
    onSuccess: async (res) => {
      clearPendingInviteToken()
      queryClient.removeQueries({ queryKey: qk.myInvites })
      navigate('/', { replace: true })
      // Land in the joined workspace; switchWorkspace re-reads the session (recheck).
      try {
        await switchWorkspace(res.workspace_id)
      } catch {
        recheck()
      }
    },
    onError: (err) => {
      if (isApiError(err) && err.status === 403 && err.code === 'invite_email_mismatch') setMismatch(true)
    },
  })

  if (!token) {
    return (
      <AuthCard title="Invite link missing" footer={<Link to="/">Go to Symone</Link>}>
        <p className="form-error" role="alert">
          Open the invite link from your email again. For your safety the link is cleared
          from the address bar after it is read, so reloading this page loses it.
        </p>
      </AuthCard>
    )
  }

  if (peekQ.isPending) {
    return (
      <AuthCard title="You’re invited">
        <p role="status" className="muted">Loading invite…</p>
      </AuthCard>
    )
  }

  if (peekQ.isError) {
    const notFound = isApiError(peekQ.error) && peekQ.error.status === 404
    return (
      <AuthCard title={notFound ? 'Invite not valid' : 'Couldn’t load invite'} footer={<Link to="/">Go to Symone</Link>}>
        <p role="alert" className="form-error">
          {notFound
            ? 'This invite has expired, was revoked, or was already used. Ask the person who invited you for a new link.'
            : 'The gateway could not be reached. Try the link again in a moment.'}
        </p>
      </AuthCard>
    )
  }

  const peek = peekQ.data
  const title = `Join ${peek.workspace_name}`

  if (inviteTokenHash) return <SetPasswordCard peek={peek} tokenHash={inviteTokenHash} />

  if (!authed) {
    return (
      <AuthCard title={title} subtitle="Sign in or create an account to accept this invite.">
        <InviteSummary peek={peek} />
        <div className="invite-actions">
          <Link className="btn btn--primary btn--block" to={withNext('/login', RETURN_PATH)}>Sign in to accept</Link>
          <Link className="btn btn--block" to={withNext('/signup', RETURN_PATH)}>Create account</Link>
        </div>
        <p className="field-hint">Use the email address the invite was sent to.</p>
      </AuthCard>
    )
  }

  const email = session?.user.email
  // null = still checking. /me/invites failed → let the server decide (accept checks
  // the email and answers 403 invite_email_mismatch).
  let isMine: boolean | null = null
  if (mismatch) isMine = false
  else if (mineQ.isError) isMine = true
  else if (mineQ.data) isMine = !!findMine(peek, mineQ.data.invites)

  if (isMine === false) {
    return (
      <AuthCard title={title}>
        <InviteSummary peek={peek} />
        <p role="alert" className="form-error">
          This invite was sent to a different email address. You’re signed in as{' '}
          <strong>{email}</strong>. Sign out, then sign in with the address the invite was sent to.
        </p>
        <div className="invite-actions">
          <button type="button" className="btn btn--block" onClick={() => void logout().then(() => navigate(withNext('/login', RETURN_PATH)))}>
            Sign out
          </button>
          <Link className="btn btn--block" to="/">Stay signed in as {email}</Link>
        </div>
      </AuthCard>
    )
  }

  return (
    <AuthCard title={title} subtitle={email ? <>Signed in as <strong>{email}</strong>.</> : undefined}>
      <InviteSummary peek={peek} />
      {accept.isError && !mismatch && <p role="alert" className="form-error">{acceptErrorMessage(accept.error)}</p>}
      <div className="invite-actions">
        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={isMine === null || accept.isPending || accept.isSuccess}
          aria-busy={accept.isPending}
          onClick={() => accept.mutate()}
        >
          {accept.isPending ? 'Accepting…' : 'Accept invite'}
        </button>
        <Link className="btn btn--block" to="/">Not now</Link>
      </div>
    </AuthCard>
  )
}
